import React, { useState, useEffect, useMemo } from 'react';
import ServicePickerMultiSelect from './ServicePickerMultiSelect';
import { validateWalkinPhone } from '../utils/walkinPhone';
import './WalkinBookingModal.css';

const WalkinBookingModal = ({
  open,
  onClose,
  services,
  selectedDate,
  settings,
  onSubmit,
  isLoading
}) => {
  // Helper: Parse time string "09:00" -> 9
  const parseTimeString = (timeStr) => {
    if (!timeStr) return null;
    const [hours] = timeStr.split(':').map(Number);
    return hours;
  };
  // C1 — Multi-service: array, not string. Single-select behavior kicks in
  // automatically when the per-store flag is off (cap collapses to 1).
  const [selectedServiceUuids, setSelectedServiceUuids] = useState([]);
  const [customerName, setCustomerName] = useState('Walk-in Customer');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [selectedHour, setSelectedHour] = useState(() => new Date().getHours());
  const [selectedMinute, setSelectedMinute] = useState(() => {
    const now = new Date();
    const mins = now.getMinutes();
    return Math.floor(mins / 15) * 15; // Round to nearest 15-minute interval
  });

  // Calculate available hours based on store settings and selected date (weekday vs weekend)
  const availableHours = useMemo(() => {
    // Fallback to 9 AM - 9 PM if no settings
    if (!settings || !settings.weekdayOpen || !settings.weekdayClose) {
      return Array.from({ length: 13 }, (_, i) => i + 9);
    }

    const date = selectedDate || new Date();
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6

    const openTime = isWeekend ? settings.weekendOpen : settings.weekdayOpen;
    const closeTime = isWeekend ? settings.weekendClose : settings.weekdayClose;

    const startHour = parseTimeString(openTime);
    const endHour = parseTimeString(closeTime);

    // Handle invalid time strings
    if (startHour === null || endHour === null) {
      return Array.from({ length: 13 }, (_, i) => i + 9);
    }

    // Generate hour array from open to close
    const hours = [];
    for (let h = startHour; h <= endHour; h++) {
      hours.push(h);
    }

    return hours.length > 0 ? hours : Array.from({ length: 13 }, (_, i) => i + 9);
  }, [settings, selectedDate]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedServiceUuids([]);
      setCustomerName('Walk-in Customer');
      setCustomerPhone('');
      setNotes('');
      setError('');
      // Reset time to current time when modal opens (if within available hours)
      const now = new Date();
      const currentHour = now.getHours();
      if (availableHours.includes(currentHour)) {
        setSelectedHour(currentHour);
      } else {
        // Default to first available hour if current time is outside operating hours
        setSelectedHour(availableHours[0] || 9);
      }
      setSelectedMinute(Math.floor(now.getMinutes() / 15) * 15);
    }
  }, [open, availableHours]);

  // Calculate appointment start time based on selected date + selected time
  const appointmentStartTime = useMemo(() => {
    const targetDate = selectedDate || new Date();
    const start = new Date(targetDate);
    start.setHours(selectedHour);
    start.setMinutes(selectedMinute);
    start.setSeconds(0);
    start.setMilliseconds(0);
    return start;
  }, [selectedDate, selectedHour, selectedMinute]);

  // C1 — Per-store flag collapses cap to 1 → single-select UI emerges naturally.
  const multiServiceEnabled = Boolean(settings?.multiServiceBookingsEnabled);
  const maxAllowed = multiServiceEnabled
    ? Math.max(1, Number(settings?.maxServicesAllowed) || 3)
    : 1;

  // C1 — End time uses summed duration across all selected services.
  const totalDurationMins = useMemo(() => {
    if (!services || selectedServiceUuids.length === 0) return 0;
    const byUuid = new Map(services.map((s) => [s.serviceUuid, s]));
    return selectedServiceUuids.reduce((sum, uuid) => {
      const svc = byUuid.get(uuid);
      return sum + (Number(svc?.appointmentDurationMins) || 30);
    }, 0);
  }, [services, selectedServiceUuids]);

  const estimatedEndTime = useMemo(() => {
    if (totalDurationMins === 0) return null;
    const endTime = new Date(appointmentStartTime.getTime() + totalDurationMins * 60000);
    return endTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }, [totalDurationMins, appointmentStartTime]);

  // Format selected date for display
  const selectedDateDisplay = useMemo(() => {
    const targetDate = selectedDate || new Date();
    const today = new Date();

    // Check if it's today
    if (targetDate.toDateString() === today.toDateString()) {
      return 'Today';
    }

    // Check if it's tomorrow
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (targetDate.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    }

    // Otherwise show the date
    return targetDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }, [selectedDate]);

  const handleSubmit = async () => {
    // Validate service selection
    if (selectedServiceUuids.length === 0) {
      setError('Please select a service');
      return;
    }

    // Validate customer name
    if (!customerName.trim()) {
      setError('Please enter a customer name');
      return;
    }

    // CP-111 (W1): phone is mandatory — the visit auto-registers against it.
    const phoneError = validateWalkinPhone(customerPhone);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    // Walk-ins represent the current moment — only block when the appointment
    // date is on a day before today. A minutes-past-now booking on today is
    // fine because the operator may be entering data after the customer
    // physically arrived.
    const startDay = new Date(appointmentStartTime);
    startDay.setHours(0, 0, 0, 0);
    const todayDay = new Date();
    todayDay.setHours(0, 0, 0, 0);
    if (startDay < todayDay) {
      setError('Cannot book appointments for past dates');
      return;
    }

    setError('');

    // Format appointmentStartTime to ISO string in LOCAL timezone (not UTC)
    // This ensures backend interprets it correctly in store's timezone
    const year = appointmentStartTime.getFullYear();
    const month = String(appointmentStartTime.getMonth() + 1).padStart(2, '0');
    const day = String(appointmentStartTime.getDate()).padStart(2, '0');
    const hours = String(appointmentStartTime.getHours()).padStart(2, '0');
    const minutes = String(appointmentStartTime.getMinutes()).padStart(2, '0');
    const seconds = String(appointmentStartTime.getSeconds()).padStart(2, '0');
    const appointmentStartTimeISO = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;

    // C1 — Multi-service payload. Server normalizer accepts `serviceUuids`
    // (preferred); legacy `serviceUuid` is also populated for one release so
    // older backends ignore the array and read the singular field.
    const payload = {
      serviceUuids: selectedServiceUuids,
      serviceUuid: selectedServiceUuids[0],
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim(),
      appointmentStartTime: appointmentStartTimeISO,
      notes: notes.trim() || null,
      walkin: true
    };

    try {
      await onSubmit(payload);
    } catch (err) {
      setError(err?.message || 'Failed to create walk-in booking');
    }
  };

  const handleServiceToggle = (serviceUuid) => {
    setError('');
    setSelectedServiceUuids((current) => {
      if (current.includes(serviceUuid)) {
        return current.filter((u) => u !== serviceUuid);
      }
      // Single-select behavior when cap is 1: replace instead of append.
      if (maxAllowed === 1) {
        return [serviceUuid];
      }
      if (current.length >= maxAllowed) {
        return current;
      }
      return [...current, serviceUuid];
    });
  };

  if (!open) return null;

  return (
    <div className="walkin-modal-overlay" onClick={onClose}>
      <div className="walkin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="walkin-modal-header">
          <h3>Add Walk-In Booking</h3>
          <button className="walkin-modal-close" onClick={onClose} disabled={isLoading}>
            ×
          </button>
        </div>

        <div className="walkin-modal-body">
          {/* Service Selection — multi-select when store flag is on */}
          <div className="walkin-modal-section">
            <label className="walkin-modal-label">
              {multiServiceEnabled ? 'Select Services' : 'Select Service'}{' '}
              <span className="required">*</span>
            </label>
            <ServicePickerMultiSelect
              services={services}
              selectedUuids={selectedServiceUuids}
              onToggle={handleServiceToggle}
              maxAllowed={maxAllowed}
              disabled={isLoading}
            />
          </div>

          {/* Customer Name */}
          <div className="walkin-modal-section">
            <label htmlFor="customer-name" className="walkin-modal-label">
              Customer Name <span className="required">*</span>
            </label>
            <input
              id="customer-name"
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Enter customer name"
              disabled={isLoading}
              className="walkin-modal-input"
              maxLength={100}
            />
          </div>

          {/* Customer Phone */}
          <div className="walkin-modal-section">
            <label htmlFor="customer-phone" className="walkin-modal-label">
              Customer Phone <span className="required">*</span>
            </label>
            <input
              id="customer-phone"
              type="tel"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              placeholder="+91 98765 43210"
              disabled={isLoading}
              className="walkin-modal-input"
              maxLength={15}
            />
          </div>

          {/* Notes */}
          <div className="walkin-modal-section">
            <label htmlFor="notes" className="walkin-modal-label">
              Notes <span className="optional">(Optional)</span>
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any special notes..."
              disabled={isLoading}
              className="walkin-modal-textarea"
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Time Picker */}
          <div className="walkin-modal-section">
            <label className="walkin-modal-label">
              Appointment Time <span className="required">*</span>
            </label>
            <div className="walkin-modal-time-picker">
              <select
                value={selectedHour}
                onChange={(e) => setSelectedHour(Number(e.target.value))}
                disabled={isLoading}
                className="walkin-modal-time-select"
              >
                {availableHours.map(hour => (
                  <option key={hour} value={hour}>
                    {hour === 0 ? '12' : hour > 12 ? hour - 12 : hour} {hour < 12 ? 'AM' : 'PM'}
                  </option>
                ))}
              </select>
              <span className="walkin-modal-time-separator">:</span>
              <select
                value={selectedMinute}
                onChange={(e) => setSelectedMinute(Number(e.target.value))}
                disabled={isLoading}
                className="walkin-modal-time-select"
              >
                <option value={0}>00</option>
                <option value={15}>15</option>
                <option value={30}>30</option>
                <option value={45}>45</option>
              </select>
            </div>
          </div>

          {/* Selected Date Display */}
          <div className="walkin-modal-date-info">
            <span className="walkin-modal-date-label">Walk-in date:</span>
            <span className="walkin-modal-date-value">{selectedDateDisplay}</span>
            <span className="walkin-modal-time-value">
              @ {appointmentStartTime.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })}
            </span>
          </div>

          {/* Estimated End Time */}
          {estimatedEndTime && (
            <div className="walkin-modal-estimated-time">
              <span className="walkin-modal-estimated-label">Estimated completion:</span>
              <span className="walkin-modal-estimated-value">{estimatedEndTime}</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="walkin-modal-error">
              {error}
            </div>
          )}
        </div>

        <div className="walkin-modal-footer">
          <button
            className="walkin-modal-cancel"
            onClick={onClose}
            disabled={isLoading}
            type="button"
          >
            Cancel
          </button>
          <button
            className="walkin-modal-submit"
            onClick={handleSubmit}
            disabled={isLoading || selectedServiceUuids.length === 0 || !customerName.trim()}
            type="button"
          >
            {isLoading ? 'Creating...' : 'Add Walk-In'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WalkinBookingModal;
